/**
 * TanStack Query hooks for a *created* concept group's detail + concepts,
 * rendered in the agent-first project right pane (OR-6126).
 *
 * Mirrors the preview queries (``feature-epi-chat``) and the sibling
 * project-page TanStack modules: every read goes through the shared
 * ``authFetch`` (``@verantos-dev/core-utils``), parsed at the HTTP trust
 * boundary with Zod, NOT the legacy
 * ``data-access-concept-groups`` RTK slice. The detail load hits the bare
 * ``GET /concept-groups/:id`` route; the flat concept list hits
 * ``GET /concept-groups/:id/concepts`` (page / pageSize / quickFilter).
 *
 * TODO: cleanup with research_assistant_gen_3 ff
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';

import { getToken } from '@verantos-dev/core-auth';
import { getBaseApiUrl, getGatewayApiVersion } from '@verantos-dev/core-constants';
import { authFetch } from '@verantos-dev/core-utils';

import type { PreviewStandardFilter } from '../conceptStandardFilter';

const CONCEPT_GROUPS_PATH = '/concept-groups';

export const conceptGroupKeys = {
  all: ['conceptGroup'] as const,
  detail: (conceptGroupId: string) => [...conceptGroupKeys.all, 'detail', conceptGroupId] as const,
  concepts: (
    conceptGroupId: string,
    page: number,
    pageSize: number,
    search: string,
    standardFilter: PreviewStandardFilter,
    sort: string,
    filter: string
  ) =>
    [
      ...conceptGroupKeys.all,
      'concepts',
      conceptGroupId,
      page,
      pageSize,
      search,
      standardFilter,
      sort,
      filter,
    ] as const,
};

// --- Schemas (parse at the HTTP trust boundary) ----------------------------

const conceptGroupDetailSchema = z.object({
  conceptGroupId: z.string(),
  conceptGroupName: z.string(),
  domains: z.string().nullish(),
  conceptCount: z.number().nullish(),
  owner: z.string().nullish(),
  // The uploaded source file's name, shown read-only on the Ready detail's
  // source-file bar (OR-6288). Absent for agent-authored groups with no upload.
  sourceFilename: z.string().nullish(),
  modifiedBy: z.object({ transactionDate: z.string().nullish() }).nullish(),
  // OR-6289: a not-yet-published *Draft* concept group carries its generative rule
  // definition (seed concepts + include/exclude/descendants flags) so the Draft preview
  // surface can show the rules alongside the expanded concepts. Absent for a published group.
  draftDefinition: z
    .array(
      z.object({
        conceptId: z.string(),
        conceptName: z.string().nullish(),
        vocabularyId: z.string().nullish(),
        includeDescendants: z.boolean().nullish(),
        includeMapped: z.boolean().nullish(),
        isExcluded: z.boolean().nullish(),
      })
    )
    .nullish(),
  // OR-6288 Stage C: location-derived asset status ("Draft" until approved,
  // "Ready" once published) — drives the Draft-review vs library rendering —
  // plus the internal Approve plumbing (confirm endpoint key + the CAS rev this
  // view is based on). Never rendered as raw values.
  assetStatus: z.string().nullish(),
  draftPreviewId: z.string().nullish(),
  draftRev: z.number().nullish(),
});
const conceptGroupDetailEnvelopeSchema = z.object({ data: conceptGroupDetailSchema });

const conceptGroupConceptSchema = z.object({
  conceptId: z.union([z.string(), z.number()]),
  conceptName: z.string().nullish(),
  conceptCode: z.string().nullish(),
  vocabularyId: z.string().nullish(),
  domainId: z.string().nullish(),
  standardConcept: z.string().nullish(),
});
const conceptGroupConceptsEnvelopeSchema = z.object({
  data: z.array(conceptGroupConceptSchema),
  pagination: z.object({ total: z.number().nullish() }).nullish(),
});

export type ConceptGroupDetail = z.infer<typeof conceptGroupDetailSchema>;
export type ConceptGroupDefinitionItem = NonNullable<ConceptGroupDetail['draftDefinition']>[number];
export type ConceptGroupConcept = z.infer<typeof conceptGroupConceptSchema>;

export interface ConceptGroupConceptsResult {
  concepts: ConceptGroupConcept[];
  total: number;
}

/** Standalone fetcher so post-upload flows can PREFETCH the canonical detail
 *  before navigating to it (no skeleton flash on arrival). */
export const fetchConceptGroupDetail = async (
  conceptGroupId: string
): Promise<ConceptGroupDetail> => {
  const json = await authFetch<unknown>(
    `${CONCEPT_GROUPS_PATH}/${encodeURIComponent(conceptGroupId)}`
  );
  return conceptGroupDetailEnvelopeSchema.parse(json).data;
};

/** Single created concept group's metadata (name / domains / owner / count). */
export const useConceptGroupDetail = (conceptGroupId: string | null) =>
  useQuery({
    queryKey: conceptGroupKeys.detail(conceptGroupId ?? ''),
    enabled: Boolean(conceptGroupId),
    queryFn: () => fetchConceptGroupDetail(conceptGroupId ?? ''),
  });

/**
 * Rename a created concept group (OR-6125 review). PATCHes
 * ``/concept-groups/:id`` then invalidates the detail + list caches so the new
 * name shows everywhere (the project page joins to the canonical row). Returns
 * void — callers refetch the canonical detail rather than trusting the PATCH
 * response shape.
 */
export const useRenameConceptGroup = (conceptGroupId: string | null) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (conceptGroupName: string): Promise<void> => {
      await authFetch<unknown>(
        `${CONCEPT_GROUPS_PATH}/${encodeURIComponent(conceptGroupId ?? '')}`,
        {
          method: 'PATCH',
          body: JSON.stringify({ conceptGroupName }),
        }
      );
    },
    onSuccess: () => {
      if (conceptGroupId) {
        queryClient.invalidateQueries({ queryKey: conceptGroupKeys.detail(conceptGroupId) });
      }
      queryClient.invalidateQueries({ queryKey: conceptGroupKeys.all });
    },
  });
};

interface UseConceptGroupConceptsArgs {
  conceptGroupId: string | null;
  page?: number;
  pageSize?: number;
  search?: string;
  standardFilter?: PreviewStandardFilter;
  /** Serialized grid sort, e.g. ``conceptName:asc``. */
  sort?: string;
  /** Serialized grid column filter, e.g. ``conceptName:contains(foo)``. */
  filter?: string;
}

/** Paged flat concept list for a created concept group — search + standard
 * filter mirror the preview concepts endpoint so the two views operate alike. */
export const useConceptGroupConcepts = ({
  conceptGroupId,
  page = 1,
  pageSize = 50,
  search = '',
  standardFilter = 'all',
  sort = '',
  filter = '',
}: UseConceptGroupConceptsArgs) =>
  useQuery({
    queryKey: conceptGroupKeys.concepts(
      conceptGroupId ?? '',
      page,
      pageSize,
      search.trim(),
      standardFilter,
      sort,
      filter
    ),
    enabled: Boolean(conceptGroupId),
    // Keep the prior page visible while the next loads so paging / typing
    // doesn't flash an empty grid.
    placeholderData: (prev) => prev,
    queryFn: async (): Promise<ConceptGroupConceptsResult> => {
      const params = new URLSearchParams({ page: String(page), pageSize: String(pageSize) });
      const term = search.trim();
      if (term) params.set('search', term);
      // The standard toggle is a dedicated param here (the created endpoint
      // reads ``standard`` directly); grid column filters go in ``filter``.
      if (standardFilter !== 'all') params.set('standard', standardFilter);
      if (filter.trim()) params.set('filter', filter.trim());
      if (sort) params.set('sort', sort);
      const json = await authFetch<unknown>(
        `${CONCEPT_GROUPS_PATH}/${encodeURIComponent(
          conceptGroupId ?? ''
        )}/concepts?${params.toString()}`
      );
      const parsed = conceptGroupConceptsEnvelopeSchema.parse(json);
      return { concepts: parsed.data, total: parsed.pagination?.total ?? 0 };
    },
  });

const CSV_HEADER = ['concept_id', 'concept_name', 'concept_code', 'vocabulary_id', 'domain_id'];

const csvCell = (value: string | number | null | undefined): string => {
  const s = value == null ? '' : String(value);
  // Quote + escape per RFC 4180 so names with commas/quotes survive.
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

/**
 * Download the full concept list as CSV. The concepts endpoint returns JSON
 * (no server CSV export for created groups), so fetch all rows
 * (``pageSize=-1`` → no limit, the gateway's "all" sentinel) and build the
 * CSV client-side.
 */
export const downloadConceptGroupCsv = async (
  conceptGroupId: string,
  fileName: string
): Promise<void> => {
  const token = await getToken();
  const url = `${getBaseApiUrl()}/${getGatewayApiVersion()}${CONCEPT_GROUPS_PATH}/${encodeURIComponent(
    conceptGroupId
  )}/concepts?page=1&pageSize=-1`;
  const response = await fetch(url, {
    headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
  });
  if (!response.ok) {
    throw new Error(`CSV export failed: ${response.status} ${response.statusText}`);
  }
  const parsed = conceptGroupConceptsEnvelopeSchema.parse(await response.json());
  const rows = parsed.data.map((c) =>
    [c.conceptId, c.conceptName, c.conceptCode, c.vocabularyId, c.domainId].map(csvCell).join(',')
  );
  const csv = [CSV_HEADER.join(','), ...rows].join('\n');

  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const objectUrl = URL.createObjectURL(blob);
  try {
    const anchor = document.createElement('a');
    anchor.href = objectUrl;
    anchor.download = fileName.endsWith('.csv') ? fileName : `${fileName}.csv`;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
};
